import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type {
  DtoV1BorrowerName,
  DtoV1IncomeIncomeSummaryCustomerEmploymentDetail,
  DtoV1IncomeRentalIncomeRentalIncomeData,
} from '~/__generated__/api';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { useSelectedAlert } from '~/stores/navigationStore';

export type IncomeRecord = Omit<
  DtoV1IncomeIncomeSummaryCustomerEmploymentDetail,
  'borrowerName'
> & { borrowerName: DtoV1BorrowerName & { fullName: string } };
export type RentalIncomeRecord = DtoV1IncomeRentalIncomeRentalIncomeData;

export function useIncomeIdentifier() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const ruleReference = useSelectedAlert((state) => state.ruleReference);
  const { customerRecordId, customerLenderDatabaseId } = useSelectedAlert();

  return useMemo(() => {
    switch (ruleReference?.type) {
      case 'employer':
        return {
          obfuscatedLoanIdentifier,
          customerIdentifier: customerRecordId
            ? {
                obfuscatedLenderDatabaseId: customerLenderDatabaseId,
                customerRecordId,
              }
            : null,
          incomeIdentifier: {
            obfuscatedLenderDatabaseId: customerLenderDatabaseId,
            customerRecordId,
            employerId: ruleReference.id,
          },
        };

      case 'address':
        return {
          obfuscatedLoanIdentifier,
          addressIdentifier: {
            addressId: ruleReference.id,
            addressLdId: ruleReference.lenderDatabaseId,
          },
        };

      default:
        return {
          obfuscatedLoanIdentifier,
        };
    }
  }, [
    customerLenderDatabaseId,
    customerRecordId,
    obfuscatedLoanIdentifier,
    ruleReference,
  ]);
}

export function useIncomeInformation(
  expectType: 'employment',
): UseQueryResult<IncomeRecord>;
export function useIncomeInformation(
  expectType: 'rental',
): UseQueryResult<RentalIncomeRecord>;
export function useIncomeInformation(): UseQueryResult<
  IncomeRecord | RentalIncomeRecord
>;

export function useIncomeInformation(): UseQueryResult<
  IncomeRecord | RentalIncomeRecord
> {
  const { obfuscatedLoanIdentifier, incomeIdentifier, addressIdentifier } =
    useIncomeIdentifier();

  return useQuery({
    queryKey: [
      'selectedIncome',
      obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
      incomeIdentifier
        ? `${incomeIdentifier.customerRecordId} ${incomeIdentifier.employerId}`
        : `${addressIdentifier?.addressId}`,
    ],
    queryFn: async function () {
      if (incomeIdentifier) {
        const response =
          await typedApiClient.v1.employmentIncomeGetCustomerEmploymentDetail({
            obfuscatedLoanIdentifier,
            incomeIdentifier,
          });

        return response.data;
      }

      if (addressIdentifier) {
        const response =
          await typedApiClient.v1.rentalIncomeValidationGetRentalIncomeData({
            obfuscatedLoanIdentifier,
            addressIdentifier,
          });
        return response.data;
      }
    },
    select: function (data): IncomeRecord | RentalIncomeRecord | null {
      if (data && 'incomeIdentifier' in data) {
        return {
          ...data,
          borrowerName: {
            ...data.borrowerName,
            fullName:
              `${data.borrowerName.firstName} ${data.borrowerName.lastName}`.trim(),
          },
        };
      }

      return data as RentalIncomeRecord | null;
    },
    enabled: !!incomeIdentifier || !!addressIdentifier,
    staleTime: 10 * 60 * 1000,
  });
}

export function isEmploymentIncome(
  income: IncomeRecord | RentalIncomeRecord | null | undefined,
): income is IncomeRecord {
  return !!income && 'incomeIdentifier' in income;
}
export function isRentalIncome(
  income: IncomeRecord | RentalIncomeRecord | null | undefined,
): income is RentalIncomeRecord {
  return !!income && 'addressIdentifier' in income;
}
